<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-card-container">
    <oxd-text tag="h6" class="orangehrm-main-title">Interview</oxd-text>
    <oxd-text tag="p" class="anna-script-intro">
      Anna's HR interview for this role: which blocks she uses, and this role's
      own questions (block 4), asked as written, in this order. The shared
      questions of the other blocks are in ANNA Settings → Interview.
    </oxd-text>
    <oxd-divider />
    <oxd-text v-if="loaded && !options" tag="p" class="anna-script-warn">
      ANNA can't be reached right now, so the personalities aren't shown. The
      role questions can still be saved.
    </oxd-text>

    <oxd-form :loading="isLoading" @submit-valid="onSave">
      <oxd-form-row>
        <oxd-grid :cols="2" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="preset"
              type="select"
              label="Fill from a dashboard role"
              :options="presetOptions"
            />
            <oxd-text tag="p" class="anna-script-help">
              Copies that role's questions, blocks and personality from the
              previous ANNA dashboard. Save to keep them.
            </oxd-text>
          </oxd-grid-item>
          <oxd-grid-item v-if="options">
            <oxd-input-field
              v-model="personality"
              type="select"
              label="Personality"
              :options="personalityOptions"
            />
          </oxd-grid-item>
          <oxd-grid-item v-if="options">
            <oxd-input-field
              v-model="department"
              type="select"
              label="Department"
              :options="departmentOptions"
            />
            <oxd-text tag="p" class="anna-script-help">
              Anna also follows this department's prompt (ANNA Settings →
              Interview → Departments) in every interview for this vacancy.
              Automatic uses the department with this vacancy's name, if there
              is one.
            </oxd-text>
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <oxd-form-row>
        <oxd-text tag="p" class="anna-script-label">Blocks Anna uses</oxd-text>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item v-for="b in blockList" :key="b.key">
            <oxd-input-field
              v-model="blocks[b.key]"
              type="switch"
              :option-label="b.label"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <oxd-form-row>
        <oxd-input-field
          v-model="roleQuestions"
          type="textarea"
          label="4. The role: this role's questions, one per line, asked as written"
          placeholder="Tell me about a time you …"
        />
      </oxd-form-row>

      <oxd-form-actions>
        <oxd-button
          display-type="secondary"
          label="Save interview"
          type="submit"
        />
      </oxd-form-actions>
    </oxd-form>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {DASHBOARD_ROLES} from './dashboard-roles';

// A vacancy's HR interview: which blocks Anna uses, its own questions (asked as written), its personality and department.
const BLOCKS = [
  {key: 'opening', label: '1. General'},
  {key: 'resume', label: '2. The resume'},
  {key: 'deepDive', label: '3. Personality'},
  {key: 'role', label: '4. The role'},
  {key: 'screening', label: 'Practical questions'},
  {key: 'ending', label: 'Ending'},
];
const allOn = () => Object.fromEntries(BLOCKS.map((b) => [b.key, true]));

export default {
  name: 'AnnaVacancyScript',
  props: {
    vacancyId: {
      type: [String, Number],
      required: true,
    },
  },
  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/anna/vacancies/${props.vacancyId}/script`,
    );
    return {http};
  },
  data() {
    return {
      isLoading: false,
      loaded: false,
      options: null,
      roleQuestions: '',
      blockList: BLOCKS,
      // a block not saved is on
      blocks: allOn(),
      personality: null,
      preset: null,
      // as saved: kept while ANNA (and so the personalities) can't be shown
      savedPersonality: null,
      department: null,
      savedDepartment: null,
      presetOptions: DASHBOARD_ROLES.map((r) => ({id: r.role, label: r.role})),
    };
  },
  computed: {
    // ANNA's departments; a saved one that's no longer there stays, marked
    departmentOptions() {
      const names = this.options?.departments || [];
      const saved = this.savedDepartment;
      return [
        {id: '', label: 'Automatic: the department named like this vacancy'},
        ...names.map((d) => ({id: d, label: d})),
        ...(saved && !names.includes(saved)
          ? [{id: saved, label: `${saved} (not in ANNA Settings any more)`}]
          : []),
      ];
    },
    personalityOptions() {
      const fallback = this.options?.defaultPersonality;
      return [
        {id: '', label: `Default${fallback ? ` (${fallback})` : ''}`},
        ...(this.options?.personalities || []).map((p) => ({id: p, label: p})),
      ];
    },
  },
  watch: {
    preset(option) {
      const role = option && DASHBOARD_ROLES.find((r) => r.role === option.id);
      if (!role) return;
      this.roleQuestions = role.roleQuestions.join('\n');
      this.blocks = {...allOn(), ...role.blocks};
      this.personality = this.personalityOption(role.personality);
      // the role's department, if ANNA Settings has one of that name
      this.department =
        this.departmentOptions.find((d) => d.id && d.id === role.role) ||
        this.department;
    },
  },
  created() {
    this.load();
  },
  methods: {
    personalityOption(name) {
      return (
        this.personalityOptions.find((p) => p.id && p.id === name) ||
        this.personalityOptions[0]
      );
    },
    apply(script) {
      this.savedPersonality = script.personality;
      this.roleQuestions = (script.roleQuestions || []).join('\n');
      this.blocks = {...allOn(), ...(script.blocks || {})};
      this.personality = this.personalityOption(script.personality);
      this.savedDepartment = script.department || null;
      this.department =
        this.departmentOptions.find(
          (d) => d.id === (script.department || ''),
        ) || this.departmentOptions[0];
    },
    load() {
      this.isLoading = true;
      this.http
        .request({method: 'GET'})
        .then(({data: {data}}) => {
          this.options = data.options;
          this.apply(data);
          this.loaded = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onSave() {
      this.isLoading = true;
      this.http
        .request({
          method: 'PUT',
          data: {
            roleQuestions: this.roleQuestions.split('\n'),
            blocks: this.blocks,
            personality: this.options
              ? this.personality?.id || null
              : this.savedPersonality,
            department: this.options
              ? this.department?.id || null
              : this.savedDepartment,
          },
        })
        .then(({data: {data}}) => {
          this.apply(data);
          this.preset = null;
          return this.$toast.saveSuccess();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.anna-script-intro,
.anna-script-help {
  color: #6a7482;
  font-size: 13px;
  line-height: 1.6;
}
.anna-script-help {
  margin-top: 4px;
  font-size: 12px;
}
.anna-script-label {
  font-size: 13px;
  font-weight: 600;
  color: #12233f;
  margin-bottom: 6px;
}
.anna-script-warn {
  color: #a05a00;
  font-size: 13px;
  margin-bottom: 12px;
}
</style>
